<template>
    <div class="pm" id="price-table">
        <table v-if="hasMethods" class="pm__table">
            <thead>
            <!-- Column labels row: Column | Column 1 | Column 2 | ... -->
            <tr>
                <th class="pm__th pm__th--rowhead pm__th--label">Column</th>
                <th
                    v-for="(q, qIdx) in quantityBreaks"
                    :key="'col-label-' + qIdx"
                    class="pm__th pm__th--label"
                    :class="{ 'pm__th--source': qIdx === saleSourceColumnIndex }"
                >
                    {{ isMobile ? 'Col. ' + (qIdx + 1) : 'Column ' + (qIdx + 1) }}
                </th>
            </tr>

            <!-- Quantity breaks row -->
            <tr>
                <th class="pm__th pm__th--rowhead">Quantity</th>
                <th
                    v-for="(q, qIdx) in quantityBreaks"
                    :key="q"
                    class="pm__th"
                    :class="{ 'pm__th--source': qIdx === saleSourceColumnIndex }"
                >
                    {{ q }}+
                </th>
            </tr>
            </thead>

            <tbody>
            <tr v-for="methodKey in methodKeys" :key="methodKey">
                <th class="pm__rowhead">
                    {{ getMethodName(methodKey) }}
                    <span
                        v-if="printMethodTooltipsEnabled && getMethodDescription(methodKey)"
                        class="fee-tooltip-wrap"
                    >
                        <span class="fee-tooltip-icon">?</span>
                        <span class="fee-tooltip-content">{{ getMethodDescription(methodKey) }}</span>
                    </span>
                </th>

                <td
                    v-for="(q, qIdx) in quantityBreaks"
                    :key="methodKey + ':' + q"
                    class="pm__td"
                    :class="{ 'pm__td--source': qIdx === saleSourceColumnIndex }"
                >
                    <template v-if="getTierForQty(methodKey, q)">
                        <template v-for="b2bTier in [getB2BTierForMethod(methodKey)]" :key="'b2b'">
                            <template v-for="b2bPrice in [functions.getB2BPriceForTier(getTierForQty(methodKey, q), b2bTier)]" :key="'price'">
                                <div class="pm__cell">
                                    <div
                                        class="pm__price pm__price--regular"
                                        :class="{
                                            'pm__price--strikethrough':
                                                b2bPrice !== null
                                                || isSalePriceValid(getTierForQty(methodKey, q))
                                        }"
                                    >
                                        {{ formatMoney(getTierForQty(methodKey, q).price) }}
                                    </div>

                                    <template v-if="b2bPrice !== null">
                                        <div class="pm__price pm__price--sale">
                                            {{ formatMoney(b2bPrice) }}
                                        </div>
                                    </template>
                                    <template v-else-if="isSalePriceValid(getTierForQty(methodKey, q))">
                                        <div class="pm__price pm__price--sale">
                                            {{ formatMoney(getTierForQty(methodKey, q).sale_price) }}
                                        </div>
                                    </template>
                                </div>
                            </template>
                        </template>
                    </template>

                    <template v-else>
                        <div class="pm__cell">
                            <span class="pm__empty">—</span>
                        </div>
                    </template>
                </td>
            </tr>
            </tbody>

            <!-- Sale badge row: no borders, flush to the price table bottom -->
            <tfoot v-if="columnsWithActiveSale.size > 0">
                <tr>
                    <td class="pm__foot-spacer"></td>
                    <td
                        v-for="(q, qIdx) in quantityBreaks"
                        :key="'foot:' + q"
                        class="pm__foot-cell"
                    >
                        <template v-if="columnsWithActiveSale.has(qIdx)">
                            <template v-for="badgeText in [getBadgeText(qIdx)]" :key="'badge'">
                                <div v-if="badgeText" class="pm__badge" :style="badgeStyle">
                                    <span class="pm__badge-line">{{ badgeText }}</span>
                                </div>
                            </template>
                        </template>
                    </td>
                </tr>
            </tfoot>
        </table>

        <div v-else class="pm__empty-state">
            No pricing data (configurable_array is empty).
        </div>
    </div>
</template>

<script>
import { useImprintStore } from '../stores/useImprintStore.js';
import { useAppStore } from '../stores/useAppStore.js';
import functions from '@/helpers/functions';
import { getImprintMethodBySku, getFallbackDescription } from '../static_data/imprintMethodDescriptions.js';

export default {
    name: 'ImprintPriceMatrix',

    data() {
        return {
            // Cache: key = `${methodKey}:${qty}` => tier|null
            tierCache: Object.create(null),
            functions: functions,
            _resizeHandler: null,
            isMobileView: typeof window !== 'undefined' && window.innerWidth <= 767
        };
    },

    computed: {
        imprintStore() {
            return useImprintStore();
        },

        appStore() {
            return useAppStore();
        },

        configurableArray() {
            return this.imprintStore.configurableArray;
        },

        methodKeys() {
            return this.imprintStore.availableImprintMethods.map(method => method.sku);
        },

        quantityBreaks() {
            return this.imprintStore.quantityBreaks;
        },

        hasMethods() {
            return this.methodKeys.length > 0;
        },

        pmConfig() {
            return window.PXUTheme?.theme_settings?.price_matrix || {};
        },

        /**
         * Returns a Set of column indices (0-based) that have at least one
         * method with an active sale_price AND a sale_price_column_text.
         * sale_price_column_text is the gatekeeper — without it nothing renders.
         */
        columnsWithActiveSale() {
            const result = new Set();
            this.quantityBreaks.forEach((qty, idx) => {
                for (const methodKey of this.methodKeys) {
                    const tier = this.getTierForQty(methodKey, qty);
                    if (tier && this.isSalePriceValid(tier) && tier.sale_price_column_text) {
                        result.add(idx);
                        break;
                    }
                }
            });
            return result;
        },

        /**
         * Returns the 0-based index of the "source" column —
         * the column whose regular prices are being matched by the sale.
         * Only active when at least one sale column has sale_price_column_text.
         *
         * Detection logic:
         *   1. No badge columns     → -1
         *   2. ALL columns on sale  → last column is the source
         *   3. Normal case          → price-match: non-sale column whose
         *                             regular price ≈ sale_price of a sale column
         *   4. Fallback             → first non-sale column after last sale column
         */
        saleSourceColumnIndex() {
            const breaks = this.quantityBreaks;
            const saleSet = this.columnsWithActiveSale;

            if (saleSet.size === 0) return -1;

            if (this.pmConfig.disable_column_highlight) return -1;

            // All columns on sale → source is the last column
            if (saleSet.size === breaks.length) {
                return breaks.length - 1;
            }

            // Price-match: find the non-sale column whose regular price equals
            // the sale_price of a sale column (tolerance 0.005 for float drift)
            for (const methodKey of this.methodKeys) {
                for (const saleIdx of saleSet) {
                    const saleTier = this.getTierForQty(methodKey, breaks[saleIdx]);
                    if (!saleTier || !this.isSalePriceValid(saleTier) || !saleTier.sale_price_column_text) continue;

                    const salePrice = Number(saleTier.sale_price);

                    for (let i = 0; i < breaks.length; i++) {
                        if (saleSet.has(i)) continue;
                        const refTier = this.getTierForQty(methodKey, breaks[i]);
                        if (!refTier) continue;
                        if (Math.abs(Number(refTier.price) - salePrice) < 0.005) {
                            return i;
                        }
                    }
                }
            }

            // Fallback: first non-sale column after the last sale column
            const lastSaleIdx = Math.max(...saleSet);
            for (let i = lastSaleIdx + 1; i < breaks.length; i++) {
                if (!saleSet.has(i)) return i;
            }

            return -1;
        },

        /**
         * Reactive mobile flag — updated in the resize handler so that
         * column labels and getBadgeText() switch on resize.
         */
        isMobile() {
            return this.isMobileView;
        },

        printMethodTooltipsEnabled() {
            return typeof window !== 'undefined'
                && window.PXUTheme?.theme_settings?.enable_price_grid_tooltips === true;
        },

        badgeStyle() {
            return {
                background: this.pmConfig.badge_background || '#FAA633',
                color: this.pmConfig.badge_text_color || '#FFFFFF'
            };
        }
    },

    watch: {
        configurableArray() {
            this.tierCache = Object.create(null);
            this.syncBadgeHeights();
        }
    },

    mounted() {
        this.syncBadgeHeights();
        this._resizeHandler = this._debounce(() => {
            this.isMobileView = window.innerWidth <= 767;
            this.syncBadgeHeights();
        }, 100);
        window.addEventListener('resize', this._resizeHandler);
    },

    beforeUnmount() {
        if (this._resizeHandler) {
            window.removeEventListener('resize', this._resizeHandler);
        }
    },

    methods: {
        getB2BTierForMethod(methodKey) {
            return this.appStore.getB2BTierForSku(methodKey);
        },

        getTiers(methodKey) {
            const method = this.configurableArray?.[methodKey] || {};
            const tiers = method?.prices_2?.tier_price || [];
            return Array.isArray(tiers) ? tiers : [];
        },

        /**
         * Returns the tier that applies to the given quantity break.
         * Picks the tier with max(QtyMin) <= qty.
         */
        getTierForQty(methodKey, qty) {
            const tiers = this.getTiers(methodKey)
                .map((t) => ({ ...t, QtyMin: Number(t?.QtyMin) }))
                .filter((t) => !Number.isNaN(t.QtyMin))
                .sort((a, b) => a.QtyMin - b.QtyMin);

            if (!tiers.length) return null;

            let best = null;
            for (let i = 0; i < tiers.length; i += 1) {
                if (tiers[i].QtyMin <= qty) best = tiers[i];
            }

            return best || tiers[0] || null;
        },

        cellTier(methodKey, qty) {
            const k = `${methodKey}:${qty}`;
            if (Object.prototype.hasOwnProperty.call(this.tierCache, k)) {
                return this.tierCache[k];
            }
            const tier = this.getTierForQty(methodKey, qty);
            this.tierCache[k] = tier;
            return tier;
        },

        formatMoney(value) {
            const n = Number(value);
            if (Number.isNaN(n)) return '';
            return `${n.toFixed(2)}`;
        },

        /**
         * Returns the display name for a method key.
         */
        getMethodName(methodKey) {
            const method = this.configurableArray?.[methodKey];
            return method?.name || methodKey;
        },

        getMethodDescription(methodKey) {
            const staticData = getImprintMethodBySku(methodKey);
            if (staticData?.description) return staticData.description;

            const liveDescription = this.configurableArray?.[methodKey]?.imprint_description;
            if (liveDescription) return liveDescription;

            return getFallbackDescription(methodKey);
        },

        /**
         * Returns true if the tier has a valid sale_price with active dates.
         */
        isSalePriceValid(tier) {
            if (!tier) return false;
            return this.functions.isValidatedSpecialPrice(tier);
        },

        /**
         * Measures all rendered .pm__badge elements and sets minHeight
         * on all of them to the tallest one, so badges stay uniform.
         * Called on mount, data change, and window resize (debounced).
         */
        syncBadgeHeights() {
            this.$nextTick(() => {
                const badges = this.$el
                    ? [...this.$el.querySelectorAll('.pm__badge')]
                    : [];
                if (!badges.length) return;

                // Reset to natural height first
                badges.forEach(b => { b.style.minHeight = ''; });

                // Find tallest
                const maxH = Math.max(...badges.map(b => b.getBoundingClientRect().height));
                if (maxH > 0) {
                    badges.forEach(b => { b.style.minHeight = maxH + 'px'; });
                }
            });
        },

        /**
         * Simple debounce — avoids pulling in lodash for one call.
         */
        _debounce(fn, delay) {
            let timer = null;
            return function (...args) {
                clearTimeout(timer);
                timer = setTimeout(() => fn.apply(this, args), delay);
            };
        },

        getBadgeText(colIdx) {
            if (this.pmConfig.disable_badge_text) return null;

            const qty = this.quantityBreaks[colIdx];
            for (const methodKey of this.methodKeys) {
                const tier = this.getTierForQty(methodKey, qty);
                if (!tier || !this.isSalePriceValid(tier) || !tier.sale_price_column_text) continue;
              //  if (colIdx === 0 && this.isMobile) return tier.sale_price_column_text;
                if (this.isMobile && tier.sale_price_column_text_mobile) {
                    return tier.sale_price_column_text_mobile;
                }
                return tier.sale_price_column_text;
            }
            return null;
        }
    }
};
</script>

<style scoped lang="scss">
@use '@/styles/components/imprint-price-matrix';
</style>
